import {
  add,
  differenceInCalendarDays,
  differenceInDays,
  differenceInMonths,
  differenceInWeeks,
  format,
  isAfter,
  isBefore,
  isEqual,
  lastDayOfMonth,
  startOfMonth,
  sub,
} from 'date-fns'

/**
 * Normalizes a date string by padding single-digit month and day values.
 * iOS Safari requires strictly ISO-8601 padded dates (e.g. "2026-07-31"),
 * and will throw a RangeError on non-padded strings like "2026-7-31".
 *
 * Returns an empty string for null/undefined inputs so callers that receive
 * runtime-nullish values (e.g. sparse query-string arrays) do not crash.
 */
export function normalizeDate(dateString: string | null | undefined): string {
  if (dateString == null)
    return ''
  const parts = dateString.split('-')
  if (parts.length !== 3)
    return dateString
  const [year, month, day] = parts
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

export interface MonthListItem {
  date: string
  firstDay: Date
  lastDay: Date
  month: string
  year: string
}

export function getMonthList(locale: string): MonthListItem[] {
  const date = new Date()
  const currentMonth = date.getMonth()
  const currentYear = date.getFullYear()
  const monthList = []
  for (let i = currentMonth; i < currentMonth + 18; i++) {
    const newDate = new Date(currentYear, i)
    monthList.push({
      date: getLocaleDateString(newDate),
      firstDay: getFirstDayOfMonth(newDate),
      lastDay: getLastDayOfMonth(newDate),
      month: newDate.toLocaleString(locale, { month: 'long' }),
      year: newDate.toLocaleString(locale, { year: 'numeric' }),
    })
  }
  return monthList
}

export function getFirstDayOfMonth(date: Date | string): Date {
  const d = new Date(typeof date === 'string' ? normalizeDate(date) : date)
  return startOfMonth(d)
}

export function getLastDayOfMonth(date: Date | string): Date {
  const d = new Date(typeof date === 'string' ? normalizeDate(date) : date)
  return lastDayOfMonth(d)
}

// Not using toISOString because it returns UTC time, giving the wrong date
export function getLocaleDateString(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

export function getMonthFromDateString(dateString: string | [string, string], locale: string): string | undefined {
  const raw = Array.isArray(dateString) ? dateString[0] : dateString
  const date = new Date(normalizeDate(raw))
  const month = date.toLocaleString(locale, { month: 'long' })

  if (month !== 'Invalid Date') {
    return month
  }
}

export function getDaysAround(date: string, numberOfDays: number): [string, string] {
  const startDate = sub(new Date(normalizeDate(date)), { days: numberOfDays })
  const endDate = add(new Date(normalizeDate(date)), { days: numberOfDays })

  return [getLocaleDateString(startDate), getLocaleDateString(endDate)]
}

export function removePastDatesFromRange(range: [string, string], date: Date): [string, string] {
  return range.map(d => (isBefore(new Date(normalizeDate(d)), date) ? getLocaleDateString(date) : d)) as [string, string]
}

export function getMiddleDate(dates: [string, string]): string {
  const startDate = new Date(normalizeDate(dates[0]))
  const endDate = new Date(normalizeDate(dates[1]))
  const diff = differenceInCalendarDays(endDate, startDate)

  return getLocaleDateString(add(startDate, { days: diff / 2 }))
}

export function isDateRangeWholeMonth(
  dateRange: [string, string],
  minDateString: string,
  maxDateString: string,
): boolean {
  const [startDateString, endDateString] = dateRange

  const startDate = new Date(normalizeDate(startDateString))
  const endDate = new Date(normalizeDate(endDateString))

  const startDateMonthAndYear = format(startDate, 'yyyy-MM')
  const endDateMonthAndYear = format(endDate, 'yyyy-MM')

  if (startDateMonthAndYear !== endDateMonthAndYear) {
    return false
  }

  const start = getFirstDayOfMonth(startDateString)
  const end = getLastDayOfMonth(endDateString)

  const minDate = new Date(normalizeDate(minDateString))
  const maxDate = new Date(normalizeDate(maxDateString))

  const minToUse = isAfter(minDate, start) ? minDate : start
  const maxToUse = isBefore(maxDate, end) ? maxDate : end

  // Format to correctly compare dates
  const formattedMin = getLocaleDateString(minToUse)
  const formattedMax = getLocaleDateString(maxToUse)
  const formattedStart = getLocaleDateString(startDate)
  const formattedEnd = getLocaleDateString(endDate)

  return (
    isEqual(new Date(formattedStart), new Date(formattedMin)) && isEqual(new Date(formattedEnd), new Date(formattedMax))
  )
}

/**
 * Calculates the distance between the given date and the current date.
 * If the distance is less than 7 days, it returns the number of days ago.
 * If the distance is less than 1 month, it returns the number of weeks ago.
 * Otherwise, it returns the formatted date in the format 'dd-M-yyyy'.
 *
 * @param date - The date to calculate the distance from.
 * @returns The distance between the given date and the current date.
 */
export function getDistanceDate(date: Date): string {
  // days --(7 days)--> weeks --(1 month)--> exact date
  // TODO: Add translations
  const now = new Date()
  const daysDifference = differenceInDays(now, date)

  if (daysDifference === 0) {
    return 'Today'
  }
  else if (daysDifference > 0 && daysDifference < 7) {
    return `${daysDifference} days ago`
  }
  else if (differenceInMonths(now, date) === 0) {
    return `${differenceInWeeks(now, date)} weeks ago`
  }

  return getLocaleDateString(date)
}
